import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Eraser, Save, Upload, Trash2, PenLine } from "lucide-react";
import { toast } from "sonner";

// Ritaglia i margini vuoti del disegno (getTrimmedCanvas della libreria non funziona in questa versione).
export function trimCanvas(src) {
  const w = src.width, h = src.height;
  const data = src.getContext("2d").getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (data[(y * w + x) * 4 + 3] > 0) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return src;
  const out = document.createElement("canvas");
  out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
  out.getContext("2d").drawImage(src, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

// "Il mio profilo" → Firma grafica: disegno (mouse/dito) o caricamento immagine. Usata dalla variabile {{utente.firma}}.
export default function FirmaGraficaDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [mode, setMode] = useState("draw");
  const [upload, setUpload] = useState("");
  const [busy, setBusy] = useState(false);
  const padRef = useRef(null);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setUpload(""); setMode("draw");
    api.get("/auth/firma").then(({ data }) => setCurrent(data.image || "")).catch(() => setCurrent(""));
  }, [open]);
  const save = async (image) => {
    setBusy(true);
    try {
      const dims = await new Promise((res) => { const im = new Image(); im.onload = () => res([im.naturalWidth, im.naturalHeight]); im.onerror = () => res([0, 0]); im.src = image; });
      const width60 = dims[1] ? Math.round((60 * dims[0]) / dims[1]) : 0;
      await api.put("/auth/firma", { image, width60 }); setCurrent(image); setUpload(""); padRef.current?.clear(); toast.success("Firma salvata");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveDrawn = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) { toast.error("Disegna la firma prima di salvare"); return; }
    save(trimCanvas(pad.getCanvas()).toDataURL("image/png"));
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) { toast.error("Carica un'immagine PNG o JPG"); return; }
    if (f.size > 500 * 1024) { toast.error("Immagine troppo grande (max 500 KB)"); return; }
    const r = new FileReader(); r.onload = () => setUpload(String(r.result || "")); r.readAsDataURL(f);
  };
  const remove = async () => {
    if (!window.confirm("Eliminare la firma salvata?")) return;
    try { await api.delete("/auth/firma"); setCurrent(""); toast.success("Firma eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const tab = (k, label, Icon) => (
    <button type="button" onClick={() => setMode(k)} data-testid={`firma-tab-${k}`}
      className={`flex-1 h-9 rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${mode === k ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="profilo-dialog">
        <DialogHeader><DialogTitle className="font-head">Il mio profilo</DialogTitle></DialogHeader>
        <div data-testid="firma-grafica-section">
          <h4 className="text-sm font-semibold text-slate-200 mb-1">Firma grafica</h4>
          <p className="text-xs text-slate-400 mb-3">Disponibile nelle lettere con la variabile «Firma utente».</p>
          <div className="rounded-lg border border-slate-700 bg-white p-3 mb-3 min-h-[80px] flex items-center justify-center" data-testid="firma-current">
            {current ? <img src={current} alt="Firma salvata" className="max-h-24 object-contain" /> : <span className="text-xs text-slate-500">Nessuna firma salvata</span>}
          </div>
          {current && <Button size="sm" variant="ghost" onClick={remove} data-testid="firma-delete" className="mb-3 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4 mr-1" /> Elimina firma</Button>}
          <div className="flex gap-1 rounded-lg border border-slate-700 p-1 mb-3">{tab("draw", "Disegna", PenLine)}{tab("upload", "Carica immagine", Upload)}</div>
          {mode === "draw" ? (
            <>
              <div className="rounded-lg border border-slate-600 bg-white" data-testid="firma-pad">
                <SignatureCanvas ref={padRef} penColor="#0f172a" canvasProps={{ className: "w-full h-40 rounded-lg touch-none", "data-testid": "firma-canvas" }} />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => padRef.current?.clear()} data-testid="firma-clear" className="border-slate-600 text-slate-200"><Eraser className="h-4 w-4 mr-1" /> Cancella</Button>
                <Button onClick={saveDrawn} disabled={busy} data-testid="firma-save-draw" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva</>}</Button>
              </div>
            </>
          ) : (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="firma-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="firma-choose-file" className="w-full border-slate-600 text-slate-200"><Upload className="h-4 w-4 mr-1" /> Scegli immagine (PNG/JPG, max 500 KB)</Button>
              {upload && (
                <div className="mt-3">
                  <div className="rounded-lg border border-slate-700 bg-white p-3 flex justify-center"><img src={upload} alt="Anteprima firma" className="max-h-24 object-contain" data-testid="firma-upload-preview" /></div>
                  <div className="flex justify-end mt-3"><Button onClick={() => save(upload)} disabled={busy} data-testid="firma-save-upload" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva</>}</Button></div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
